import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Drawer, DrawerField, DrawerGroup, drawerInput } from "../Drawer";
import { Toggle } from "../Toggle";
import { PageLoader } from "../PageLoader";
import { ConfirmDialog } from "../crm/ui";
import { AccessNotice, fetchApi, reasonOf } from "../marketing/access";

/**
 * Support → complaint or feedback categories (the dropdowns on the complaint form, the feedback form
 * and both lists' Category filters). No screen managed them before, old or new.
 *
 *   list ...... GET    /api/complaint-categories  | /api/feedback-categories   → { data: [{ id, name, description, is_active }] }
 *   add ....... POST   …                          { name, description }       (409 when the name exists)
 *   edit ...... PUT    …/:id                      { name, description, is_active }
 *   delete .... DELETE …/:id                      (400 while a complaint / feedback still uses it: its message is shown;
 *                                                  turning the category off hides it from new entries instead)
 *
 * Props: open, onClose, kind ("complaint" | "feedback"), onChanged() after any save.
 */
const PATH = { complaint: "/api/complaint-categories", feedback: "/api/feedback-categories" };
const blank = { id: null, name: "", description: "" };

export default function CategoriesDrawer({ open, onClose, kind, onChanged }) {
  const path = PATH[kind];
  const noun = kind === "complaint" ? "complaint" : "feedback";
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(blank);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoadError(null);
    const r = await fetchApi(path);
    if (!r.ok) { setRows([]); setLoadError(reasonOf(r)); return; }
    setRows(r.data?.data || []);
  }, [path]);
  useEffect(() => { if (open) { setRows(null); setForm(blank); setError(""); load(); } }, [open, load]);

  const done = (message) => { setError(""); load(); onChanged?.(message); };
  const save = async () => {
    const name = form.name.trim();
    if (!name) { setError("Give the category a name."); return; }
    setBusy("save");
    const r = await fetchApi(form.id ? `${path}/${form.id}` : path, { method: form.id ? "put" : "post", body: { name, description: form.description.trim() } });
    setBusy("");
    if (!r.ok) { setError(r.message || "Couldn't save the category."); return; }
    setForm(blank);
    done(form.id ? `${name} saved` : `${name} added`);
  };
  const toggle = async (row) => {
    setBusy(`t${row.id}`);
    const r = await fetchApi(`${path}/${row.id}`, { method: "put", body: { is_active: !row.is_active } });
    setBusy("");
    if (!r.ok) { setError(r.message || "Couldn't change the category."); return; }
    done(`${row.name} turned ${row.is_active ? "off" : "on"}`);
  };
  const remove = async () => {
    const row = confirm;
    setConfirm(null);
    setBusy(`d${row.id}`);
    const r = await fetchApi(`${path}/${row.id}`, { method: "delete" });
    setBusy("");
    if (!r.ok) { setError(`${r.message || "Couldn't delete the category."}${r.status === 400 ? " Turn it off instead to hide it from new entries." : ""}`); return; }
    if (form.id === row.id) setForm(blank);
    done(`${row.name} deleted`);
  };

  return (
    <Drawer open={open} onClose={onClose} title={`${kind === "complaint" ? "Complaint" : "Feedback"} categories`} context="Support setup" closeLabel="Close">
      <div className="flex flex-col gap-5">
        <DrawerGroup title={form.id ? "Edit category" : "Add a category"} hint={`Shown on the ${noun} form and in the list's Category filter.`}>
          <DrawerField label="Name"><input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") save(); }} maxLength={100} className={drawerInput} placeholder={kind === "complaint" ? "Billing, Waiting time…" : "Treatment, Front desk…"} /></DrawerField>
          <DrawerField label="Description" optional><input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={drawerInput} /></DrawerField>
          <div className="col-span-2 flex gap-2"><button type="button" onClick={save} disabled={busy === "save"} className="flex h-10 items-center gap-1.5 rounded-full bg-ink px-4 text-small font-semibold text-paper disabled:opacity-50">{form.id ? "Save category" : <><Plus size={15} />Add category</>}</button>{form.id && <button type="button" onClick={() => { setForm(blank); setError(""); }} className="h-10 rounded-full px-4 text-small font-semibold hover:bg-mist">Cancel</button>}</div>
          {error && <p role="alert" className="col-span-2 text-caption text-danger">{error}</p>}
        </DrawerGroup>
        <DrawerGroup title="Categories">
          <div className="col-span-2">
            {rows === null ? <PageLoader label="Loading categories…" className="py-12" /> : loadError ? <AccessNotice reason={loadError} onRetry={load} compact /> : rows.length === 0 ? <p className="rounded-sm bg-mist px-4 py-6 text-center text-small text-muted">No categories yet. Add the first one above.</p> : (
              <ul className="flex flex-col divide-y divide-line rounded-sm border border-line">
                {rows.map((row) => <li key={row.id} className="flex items-center gap-3 px-3 py-2.5">
                  <div className="min-w-0 flex-1"><p className={`truncate text-small font-semibold ${row.is_active ? "" : "text-muted"}`}>{row.name}{!row.is_active && <span className="ml-2 text-caption font-normal">Off</span>}</p>{row.description && <p className="truncate text-caption text-muted">{row.description}</p>}</div>
                  <Toggle checked={!!row.is_active} label={`${row.name} in use`} disabled={busy === `t${row.id}`} onChange={() => toggle(row)} />
                  <button type="button" aria-label={`Edit ${row.name}`} onClick={() => { setForm({ id: row.id, name: row.name || "", description: row.description || "" }); setError(""); }} className="flex h-8 w-8 items-center justify-center rounded-full text-muted hover:bg-mist hover:text-ink"><Pencil size={15} strokeWidth={1.75} /></button>
                  <button type="button" aria-label={`Delete ${row.name}`} disabled={busy === `d${row.id}`} onClick={() => setConfirm(row)} className="flex h-8 w-8 items-center justify-center rounded-full text-muted hover:bg-mist hover:text-danger"><Trash2 size={15} strokeWidth={1.75} /></button>
                </li>)}
              </ul>
            )}
          </div>
        </DrawerGroup>
      </div>
      {confirm && <ConfirmDialog title="Delete this category?" body={<p>Delete {confirm.name}? A category still used by a {noun} can't be deleted; turn it off instead.</p>} confirmLabel="Delete category" onCancel={() => setConfirm(null)} onConfirm={remove} />}
    </Drawer>
  );
}
